import { useCallback, useEffect, useRef, useState } from "react";

export type LocalAutosaveStatus = "idle" | "saving" | "saved" | "error";

type PersistedPayload<T> = {
  data: T;
  savedAt: string | null;
};

function parsePersistedPayload<T>(raw: string | null): PersistedPayload<T> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      "data" in parsed &&
      "savedAt" in parsed
    ) {
      return parsed as PersistedPayload<T>;
    }
    return { data: parsed as T, savedAt: null };
  } catch {
    return null;
  }
}

function useLocalDraftAutosave<T>(
  storageKey: string,
  initialValueFactory: () => T,
  delayMs = 850,
) {
  const getPersisted = () => {
    if (typeof window === "undefined") return null;
    return parsePersistedPayload<T>(window.localStorage.getItem(storageKey));
  };

  const [value, setValue] = useState<T>(() => {
    const persisted = getPersisted();
    return persisted ? persisted.data : initialValueFactory();
  });
  const [hasPersistedData] = useState<boolean>(() => !!getPersisted());

  const [status, setStatus] = useState<LocalAutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(() => {
    const persisted = getPersisted();
    return persisted?.savedAt ?? null;
  });

  const initializedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const flushNow = useCallback(
    (nextValue: T) => {
      if (typeof window === "undefined") return;
      try {
        const savedAt = new Date().toISOString();
        const payload: PersistedPayload<T> = {
          data: nextValue,
          savedAt,
        };
        window.localStorage.setItem(storageKey, JSON.stringify(payload));
        setLastSavedAt(savedAt);
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    },
    [storageKey],
  );

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      setStatus("saved");
      return;
    }

    setStatus("saving");

    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      flushNow(value);
      timerRef.current = null;
    }, delayMs);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [value, delayMs, flushNow]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        flushNow(value);
      }
    };

    const handleBeforeUnload = () => {
      flushNow(value);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [flushNow, value]);

  const clearPersisted = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(storageKey);
      setLastSavedAt(null);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }, [storageKey]);

  return {
    value,
    setValue,
    status,
    lastSavedAt,
    hasPersistedData,
    clearPersisted,
  };
}

export default useLocalDraftAutosave;
